import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap, type RetentionClass } from "../api/client";

const days = (n?: number) =>
  n === undefined ? "…" : `${n} day${n === 1 ? "" : "s"}`;

const BASES = [
  { value: "recorded", label: "From when it was recorded" },
  { value: "case_closed", label: "From when the case was closed" },
];

export function describeClass(c: RetentionClass) {
  const years = c.days % 365 === 0 ? c.days / 365 : null;
  const period = years
    ? `${years} year${years === 1 ? "" : "s"}`
    : `${c.days} days`;
  return `${period} ${c.basis === "case_closed" ? "after the case is closed" : "from recording"}`;
}

/** Retention classes and the default for material in no case (threat model §2.2). */
export function RetentionSettingsPage() {
  const qc = useQueryClient();
  const classes = useQuery({
    queryKey: ["retention", "classes"],
    queryFn: () => unwrap(api.GET("/api/retention/classes")),
  });
  const settings = useQuery({
    queryKey: ["retention", "settings"],
    queryFn: () => unwrap(api.GET("/api/retention/settings")),
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["retention"] });
  const [editing, setEditing] = useState<RetentionClass | null>(null);
  const [name, setName] = useState("");
  const [basis, setBasis] = useState("recorded");
  const [period, setPeriod] = useState("");
  const [unassigned, setUnassigned] = useState("");
  const [warning, setWarning] = useState("");

  const reset = () => {
    setEditing(null);
    setName("");
    setBasis("recorded");
    setPeriod("");
  };
  const save = useMutation({
    mutationFn: () => {
      const body = { name, basis, days: Number(period) };
      return unwrap(
        editing
          ? api.PUT("/api/retention/classes/{id}", {
              params: { path: { id: editing.id } },
              body,
            })
          : api.POST("/api/retention/classes", { body }),
      );
    },
    onSuccess: () => {
      reset();
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.DELETE("/api/retention/classes/{id}", { params: { path: { id } } }),
      ),
    onSuccess: refresh,
  });
  const saveDefaults = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT("/api/retention/settings", {
          body: {
            unassigned_days: Number(
              unassigned || settings.data?.unassigned_days,
            ),
            warning_days: Number(warning || settings.data?.warning_days),
          },
        }),
      ),
    onSuccess: () => {
      setUnassigned("");
      setWarning("");
      refresh();
    },
  });

  return (
    <Stack spacing={3} component="section" aria-label="Retention settings">
      <Typography variant="h5" component="h2">
        Retention settings
      </Typography>
      <Typography color="text.secondary">
        Each case is given a retention class. Recordings in no case are kept for
        the default below. When a recording falls due it is listed on the
        Retention page for review; nothing is deleted without two people.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" component="h3" gutterBottom>
          Recordings in no case
        </Typography>
        <Stack
          direction="row"
          sx={{ gap: 2, flexWrap: "wrap", alignItems: "center" }}
        >
          <TextField
            size="small"
            type="number"
            label="Keep for (days)"
            placeholder={String(settings.data?.unassigned_days ?? "")}
            value={unassigned}
            onChange={(e) => setUnassigned(e.target.value)}
            helperText={`Now ${days(settings.data?.unassigned_days)}`}
          />
          <TextField
            size="small"
            type="number"
            label="Review looks ahead (days)"
            placeholder={String(settings.data?.warning_days ?? "")}
            value={warning}
            onChange={(e) => setWarning(e.target.value)}
            helperText={`Now ${days(settings.data?.warning_days)}`}
          />
          <Button
            variant="contained"
            disabled={(!unassigned && !warning) || saveDefaults.isPending}
            onClick={() => saveDefaults.mutate()}
          >
            Save
          </Button>
        </Stack>
        {saveDefaults.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveDefaults.error.message}
          </Alert>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" component="h3" gutterBottom>
          Retention classes
        </Typography>
        {classes.data?.length === 0 ? (
          <Typography color="text.secondary" gutterBottom>
            No classes yet. Until a case has one, its recordings are kept.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small" aria-label="Retention classes">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Kept</TableCell>
                  <TableCell>Cases</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {classes.data?.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>{describeClass(c)}</TableCell>
                    <TableCell>{c.cases}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setEditing(c);
                          setName(c.name);
                          setBasis(c.basis);
                          setPeriod(String(c.days));
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        disabled={c.cases > 0 || remove.isPending}
                        onClick={() => remove.mutate(c.id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Stack
          component="form"
          direction="row"
          sx={{ gap: 2, flexWrap: "wrap", alignItems: "center", mt: 2 }}
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <TextField
            size="small"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            size="small"
            select
            label="Counted"
            value={basis}
            onChange={(e) => setBasis(e.target.value)}
            sx={{ minWidth: 260 }}
          >
            {BASES.map((b) => (
              <MenuItem key={b.value} value={b.value}>
                {b.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            type="number"
            label="Days"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            helperText="365 a year"
          />
          <Button
            type="submit"
            variant="contained"
            disabled={!name.trim() || !Number(period) || save.isPending}
          >
            {editing ? "Save class" : "Add class"}
          </Button>
          {editing && <Button onClick={reset}>Cancel</Button>}
        </Stack>
        {(save.isError || remove.isError) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(save.error ?? remove.error)?.message}
          </Alert>
        )}
      </Paper>
    </Stack>
  );
}
